import type { CharacterDto, ProjectDto, ShotData } from '@studio/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, RefreshCw, Sparkles, Square, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useProjectCall } from '@/components/project/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { isActive, useCharacters } from '@/lib/queries';

const VIEWS = ['Front', 'Three-quarter', 'Face'];

/** Everyone the project's shots show or name, in cast order first. */
export function projectCharacters(project: ProjectDto, characters: CharacterDto[]): CharacterDto[] {
  const ids = new Set<string>(project.castIds);
  for (const s of project.shots) {
    if (s.kind !== 'shot') continue;
    const d = s.data as ShotData;
    d.cast.forEach((m) => m.type === 'character' && ids.add(m.id));
    d.lines.forEach((l) => l.characterId && ids.add(l.characterId));
  }
  const byId = new Map(characters.map((c) => [c.id, c]));
  return [...ids].map((id) => byId.get(id)).filter((c): c is CharacterDto => !!c);
}

/**
 * Step 1 of a film: the characters. Each gets a sheet of three pictures (front, three-quarter, face) drawn on the GPU
 * and approved by the team. Every storyboard still and every shot with two or more people carries these pictures,
 * tied to the person's lines, so FreeVideo knows who is who and who speaks.
 */
export function CharactersTab({ project }: { project: ProjectDto }) {
  const call = useProjectCall(project.id);
  const qc = useQueryClient();
  const { me } = useAuth();
  const canDraw = me?.imagesAvailable !== false;
  const { data: all = [] } = useCharacters();
  const people = projectCharacters(project, all);
  const ready = people.filter((c) => c.sheetApproved).length;
  const incomplete = people.filter((c) => !c.sheetApproved && c.images.length < 3 && !(c.castJob && isActive(c.castJob.status)));
  const refresh = (r?: CharacterDto) => {
    if (r) qc.setQueryData<CharacterDto[]>(['characters'], (list) => list?.map((x) => (x.id === r.id ? r : x)));
    void qc.invalidateQueries({ queryKey: ['characters'] });
    void qc.invalidateQueries({ queryKey: ['project', project.id] });
    void qc.invalidateQueries({ queryKey: ['gpu'] });
  };
  const charCall = async (path: string, body?: unknown) => {
    try {
      refresh(await api<CharacterDto>(path, { method: 'POST', body }));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };
  const makeAll = async () => {
    const r = await call<{ queued: number; skipped: string[]; project: ProjectDto }>(
      `/api/projects/${project.id}/cast-pictures`,
      { method: 'POST' },
      (x) => x.project,
    );
    if (r) {
      refresh();
      if (r.skipped.length) toast.warning(`No description, so nothing drawn: ${r.skipped.join(', ')}`);
    }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <div className="text-sm font-medium">
            {ready} of {people.length} characters approved
          </div>
          <p className="max-w-3xl text-xs text-muted-foreground">
            Make the characters first. Each gets three pictures — front, three-quarter and face — drawn from one
            another so they match. Approve them, then make the storyboard: every still, and every shot with two or more
            people, carries these pictures tied to each person's lines, so the video knows who is who and who speaks.
          </p>
        </div>
        {incomplete.length > 0 && canDraw && (
          <Button onClick={() => void makeAll()}>
            <Sparkles /> Make {incomplete.length} character sheet{incomplete.length > 1 ? 's' : ''}
          </Button>
        )}
      </div>
      {!people.length && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No characters in this project yet — add them in Settings on the Script tab, or in the shots.
        </Card>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        {people.map((c) => {
          const busy = !!c.castJob && isActive(c.castJob.status);
          return (
            <Card key={c.id} className={`space-y-2 p-3 ${c.sheetApproved ? 'ring-2 ring-success' : ''}`}>
              <div className="flex items-center gap-2">
                <span className="font-medium">{c.name}</span>
                {c.sheetApproved ? (
                  <Badge variant="success">
                    <Check className="size-3" /> approved
                  </Badge>
                ) : c.images.length ? (
                  <Badge variant="warning">not approved</Badge>
                ) : (
                  <Badge variant="secondary">no pictures</Badge>
                )}
                <Link to="/characters" className="ml-auto text-xs text-muted-foreground underline">
                  edit look / voice
                </Link>
              </div>
              <p className="line-clamp-2 text-xs text-muted-foreground">{c.visualDescription || 'No description yet.'}</p>
              {(c.outfitImage || c.background) && (
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  {c.outfitImage && (
                    <span className="flex items-center gap-1.5" title="The sheet is drawn wearing these clothes">
                      <img src={c.outfitImage.url} alt="Outfit" className="size-8 rounded object-cover" /> Outfit
                    </span>
                  )}
                  {c.background && (
                    <span className="flex items-center gap-1.5" title="Every still and shot with this character is set here">
                      {c.background.image && <img src={c.background.image.url} alt="" className="h-8 w-12 rounded object-cover" />}
                      🔒 {c.background.name}
                    </span>
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                {VIEWS.map((label, i) => (
                  <div key={label} className="space-y-1">
                    <div className="relative aspect-[9/16] overflow-hidden rounded-md bg-muted">
                      {c.images[i] ? (
                        <a href={c.images[i]!.url} target="_blank" rel="noreferrer">
                          <img src={c.images[i]!.url} alt={`${c.name} ${label}`} className="size-full object-cover" />
                        </a>
                      ) : (
                        <div className="grid size-full place-items-center text-muted-foreground">
                          {busy ? <Loader2 className="size-5 animate-spin" /> : <UserRound className="size-5" />}
                        </div>
                      )}
                    </div>
                    <div className="text-center text-[11px] text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
              {c.castJob?.status === 'failed' && <p className="text-xs text-destructive">{c.castJob.error}</p>}
              <div className="flex flex-wrap gap-1">
                {busy ? (
                  <>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Loader2 className="size-3.5 animate-spin" /> {c.castJob?.progressNote ?? 'Drawing…'}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto h-7 px-2 text-xs"
                      onClick={() => void charCall(`/api/characters/${c.id}/cast-picture/cancel`)}
                    >
                      <Square /> Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    {canDraw && (
                      <Button
                        size="sm"
                        variant={c.images.length >= 3 ? 'outline' : 'default'}
                        className="h-7 px-2 text-xs"
                        onClick={() => void charCall(`/api/characters/${c.id}/cast-picture`, { style: project.style })}
                        title={c.images.length ? 'Keeps the front picture; draws the other two from it' : 'Draws all three from the description'}
                      >
                        <Sparkles /> {c.images.length ? (c.images.length >= 3 ? 'Redo side views' : 'Make side views') : 'Make sheet'}
                      </Button>
                    )}
                    {canDraw && c.images.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-xs"
                        onClick={() =>
                          void charCall(`/api/characters/${c.id}/cast-picture`, { style: project.style, redrawFront: true })
                        }
                        title="Draws a new front from the description, then the other two from it"
                      >
                        <RefreshCw /> Redraw all
                      </Button>
                    )}
                    {c.images.length > 0 && (
                      <Button
                        size="sm"
                        variant={c.sheetApproved ? 'outline' : 'default'}
                        className="ml-auto h-7 px-2 text-xs"
                        onClick={() => void charCall(`/api/characters/${c.id}/approve`, { approved: !c.sheetApproved })}
                      >
                        <Check /> {c.sheetApproved ? 'Unapprove' : 'Approve'}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
